// Phase B — Konsolidierter Backup & Datensicherung Hub.
// Bündelt die früher verstreuten Backup-UIs (BackupCenter, SystemBackupRestorePanel,
// DisasterRecoveryPanel, BackupHealthCard) in EINEM Admin-Only-Tab mit Sub-Tabs.
//
// Zugriff: nur Rolle `admin` (über `einstellungen_backup`-Permission-Gate +
// zusätzlich `isAdmin`-Check im Parent).

import { useState } from "react";
import { ShieldCheck, Database, AlertTriangle, Activity } from "lucide-react";
import BackupCenter from "@/components/BackupCenter";
import SystemBackupRestorePanel from "@/components/SystemBackupRestorePanel";
import DisasterRecoveryPanel from "@/components/DisasterRecoveryPanel";
import BackupHealthCard from "@/components/BackupHealthCard";

const SUBTABS = [
  {
    key: "backups",
    label: "Backups",
    icon: Database,
    description: "Datenbank-Bereiche als JSON/ZIP herunterladen · Wöchentliche E-Mail-Sicherung.",
  },
  {
    key: "system",
    label: "System-Backup & Restore",
    icon: ShieldCheck,
    description: "Vollständige System-Backups erstellen, hochladen oder wiederherstellen.",
  },
  {
    key: "disaster",
    label: "Disaster Recovery",
    icon: AlertTriangle,
    description: "Komplett-Export inkl. Dateien, Vendor-Bundles und Restore-Skripten.",
  },
  {
    key: "health",
    label: "Backup-Health",
    icon: Activity,
    description: "Status, Alter und Größe der letzten automatischen Sicherung.",
  },
];

export default function BackupDataHub() {
  const [sub, setSub] = useState("backups");
  const current = SUBTABS.find((s) => s.key === sub) || SUBTABS[0];

  return (
    <div className="space-y-6" data-testid="backup-data-hub">
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-amber-200">Admin-Only Bereich</div>
            <div className="text-xs text-amber-300/80 mt-0.5">
              Diese Sektion bündelt alle Sicherungs- und Wiederherstellungs-Werkzeuge.
              Geschäftsführung und andere Rollen haben hier <b>keinen</b> Zugriff.
            </div>
          </div>
        </div>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="border-b border-zinc-800">
        <div className="flex gap-1 flex-wrap">
          {SUBTABS.map((t) => {
            const Icon = t.icon;
            const active = sub === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setSub(t.key)}
                data-testid={`backup-hub-subtab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? "text-zinc-50 border-amber-500"
                    : "text-zinc-400 border-transparent hover:text-zinc-200"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="text-xs text-zinc-500">{current.description}</div>

      {/* Sub-Tab Content */}
      <div data-testid={`backup-hub-content-${sub}`}>
        {sub === "backups" && <BackupCenter />}
        {sub === "system" && <SystemBackupRestorePanel />}
        {sub === "disaster" && <DisasterRecoveryPanel />}
        {sub === "health" && <BackupHealthCard />}
      </div>
    </div>
  );
}
